/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppSelector } from '../../reducers';
import { generateUrl } from '../../routes/util';
import G17Client from '../../services/G17Client';
import { CustomReport, CustomReportType, NewMetrics } from '../../types/custom-report';
import { SubmitButton } from '../button/SubmitButton';
import { FieldProps, FormGenerator } from '../form/FormGenerator';
import Loader from '../loader';
import { CustomReportRoutes } from './CustomReportsRoot';
import './styles.scss'

const fields: FieldProps[] = [
  {
    code: 'name',
    type: 'text',
    label: 'Name of custom report',
    placeholder: `For example: Gender diversity report for CSR ${(new Date()).getFullYear()}`,
    required: true,
  },
  {
    code: 'description',
    type: 'textarea',
    label: 'Description',
    placeholder: '',
  },
];

interface FormData {
  _id?: string;
  name?: string;
  description?: string;
}

const getInitialState = (customReport?: CustomReport): FormData => {
  return {
    _id: customReport?._id,
    name: customReport?.name,
    description: customReport?.description,
  }
};

interface CustomReportEditFormProps {
  customReport?: CustomReport;
}

const CustomReportEditForm = (props: CustomReportEditFormProps) => {
  const { customReport } = props;

  const [formData, setFormData] = useState<FormData | undefined>(getInitialState(customReport));
  const [isSaving, setSaving] = useState(false);
  const initiativeState = useAppSelector((state) => state.initiative);

  const history = useHistory();

  React.useEffect(() => {
    setFormData(getInitialState(customReport));
  }, [customReport]);

  if (!initiativeState.loaded) {
    return <Loader />;
  }

  const initiativeId = initiativeState.id;
  const isFormValid = Boolean(formData?.name);

  const handleUpdate = (e: React.ChangeEvent<{ name: string, value: string | number | undefined }>) => {
    if (!fields.map(f => f.code).includes(e.target.name)) {
      return;
    }

    setFormData({
      ...(formData ?? {}),
      [e.target.name]: e.target.value
    });
  };

  const isEditMode = Boolean(formData?._id);

  const handleSubmit = async () => {
    if (!formData || !formData.name) {
      return;
    }

    const data: NewMetrics = {
      type: CustomReportType.Metrics,
      name: formData.name,
      description: formData?.description
    }
    setSaving(true);

    const method = formData._id ? G17Client.updateCustomReport(initiativeId, formData._id, data) : G17Client.createCustomReport(initiativeId, data);
    method
      .then((data) => {
        const url = generateUrl(CustomReportRoutes.MANAGE_REPORT, { initiativeId, reportId: data._id });
        setSaving(false);
        setFormData(undefined);
        history.push(url);
      })
      .catch(() => {
        setSaving(false);
      });
  }

  return <>
    <FormGenerator
      form={formData ?? {}}
      fields={fields.map(f => ({ ...f, disabled: isSaving }))}
      updateForm={handleUpdate}
    />
    <div className='mt-4 text-right'>
      <SubmitButton
        disabled={!isFormValid || isSaving}
        color='primary'
        onClick={handleSubmit}>
        {isEditMode ? 'Update' : 'Create'}
      </SubmitButton>
    </div>
  </>
}

export default CustomReportEditForm;
